import React, { useEffect } from "react";
import { X, Sparkles, MessageSquare, TrendingUp, TrendingDown, ArrowRightLeft, Trash2 } from "lucide-react";
import { Tile, Badge, StatusDot } from "./Primitives";
import { formatPeso, cn } from "../lib/utils";
import { useCopilot } from "../lib/copilot.jsx";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const CAL_COLORS = ["rgb(var(--success) / 0.4)", "rgb(var(--warning) / 0.6)", "rgb(var(--critical) / 0.7)", "rgb(var(--critical))"];

export default function InventoryDrillDrawer({ open, drill, hotspotType, onClose }) {
  const { askCopilot } = useCopilot();
  const typeLabel = {
    "kpi": "KPI Deep-Dive",
    "abcxyz": "ABC × XYZ Cell",
    "stockout-sku": "Stockout SKU Timeline",
    "deadstock": "Dead-Stock Recovery",
    "replen": "Replen Queue Item",
  }[hotspotType] || hotspotType;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !drill) return null;

  const briefing = `As an executive, brief me deeply on "${drill.title}". Subtitle: ${drill.subtitle}. Key metrics: ${drill.kpis.map(k => `${k.label} = ${k.value}${typeof k.delta === "number" ? ` (${k.delta >= 0 ? "+" : ""}${(k.delta * 100).toFixed(1)}%)` : ""}`).join("; ")}. AI summary: ${drill.aiExplanation}. Walk me through: (1) the 3 highest-ROI actions I should approve this week, (2) which other Control Tower modules corroborate this signal, (3) what the ${drill.confidence ? `${(drill.confidence * 100).toFixed(0)}%` : ""} confidence really means for decisioning, (4) risks not captured in the summary.`;

  return (
    <>
      {/* backdrop */}
      <div
        data-testid="inv-drill-backdrop"
        onClick={onClose}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in"
      />
      {/* drawer */}
      <aside
        data-testid="inv-drill-drawer"
        className="fixed top-0 right-0 h-screen w-full sm:w-[520px] lg:w-[640px] xl:w-[720px] bg-bg border-l border-border shadow-2xl z-50 overflow-y-auto animate-slide-in-right"
      >
        <div className="sticky top-0 bg-bg/95 backdrop-blur-md border-b border-border px-5 py-3.5 flex items-start justify-between gap-4 z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-1">Inventory Drill-Down · {typeLabel}</div>
            <h2 className="font-display text-lg font-semibold tracking-tight leading-tight">{drill.title}</h2>
            <p className="text-[12px] text-muted mt-0.5">{drill.subtitle}</p>
          </div>
          <button
            data-testid="inv-drill-close"
            onClick={onClose}
            className="w-8 h-8 shrink-0 inline-flex items-center justify-center border border-border hover:border-primary hover:text-primary rounded-sm"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* KPI strip */}
          <div className="tile grid grid-cols-2 md:grid-cols-4 divide-x divide-border" data-testid="inv-drill-kpis">
            {drill.kpis.map((k, i) => (
              <div key={i} className="px-3 py-2.5">
                <div className="kpi-label flex items-center gap-1.5 text-[10px]">{k.status && <StatusDot status={k.status} />}{k.label}</div>
                <div className="kpi-value text-lg mt-1">{k.value}</div>
                {typeof k.delta === "number" && (
                  <div className={cn("text-[10px] font-mono-tabular mt-0.5", k.delta >= 0 ? "text-success" : "text-critical")}>
                    {k.delta >= 0 ? <TrendingUp size={9} className="inline" /> : <TrendingDown size={9} className="inline" />}
                    {" "}{k.delta >= 0 ? "+" : ""}{(k.delta * 100).toFixed(1)}%
                  </div>
                )}
              </div>
            ))}
          </div>

          {hotspotType === "abcxyz" && drill.compliance && (
            <Tile title="Policy Compliance Breakdown" testid="inv-drill-compliance"
                  action={<Badge status={drill.kpis[3]?.status || "warning"}>Overall {drill.kpis[3]?.value}</Badge>}>
              <div className="space-y-3">
                {drill.compliance.map((m, i) => {
                  const gap = m.value < m.target;
                  const tone = m.value >= 0.85 ? "success" : m.value >= 0.65 ? "warning" : "critical";
                  const barBg = tone === "success" ? "bg-success" : tone === "warning" ? "bg-warning" : "bg-critical";
                  return (
                    <div key={i} data-testid={`comp-metric-${i}`}>
                      <div className="flex items-baseline justify-between mb-1">
                        <div>
                          <span className="text-[12.5px] font-medium">{m.label}</span>
                          <span className="text-[10px] text-muted ml-2">{m.hint}</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className={cn("font-mono-tabular text-sm font-semibold",
                            tone === "success" ? "text-success" : tone === "warning" ? "text-warning" : "text-critical")}>
                            {(m.value * 100).toFixed(0)}%
                          </span>
                          <span className="text-[10px] text-subtle font-mono-tabular">/ tgt {(m.target * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                      <div className="relative h-2 bg-surface-2 rounded-sm overflow-hidden">
                        <div className={cn("h-full transition-all", barBg)} style={{ width: `${m.value * 100}%` }} />
                        {/* Target tick */}
                        <div className="absolute top-0 bottom-0 w-px bg-fg/70" style={{ left: `${m.target * 100}%` }} title={`Target ${(m.target * 100).toFixed(0)}%`} />
                      </div>
                      {gap && (
                        <div className="text-[10px] text-critical mt-1 font-mono-tabular">Gap: {((m.target - m.value) * 100).toFixed(0)}pp below target</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Tile>
          )}

          {/* Type-specific body */}
          {hotspotType === "stockout-sku" && drill.timeline && (
            <Tile title="90-Day Stockout Timeline" testid="inv-drill-timeline">
              <div style={{ height: 160 }}>
                <ResponsiveContainer>
                  <BarChart data={drill.timeline} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                    <CartesianGrid stroke="rgb(var(--border))" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 9, fill: "rgb(var(--muted))" }} interval={14} />
                    <YAxis hide />
                    <Tooltip cursor={{ fill: "rgb(var(--surface-2))" }} />
                    <Bar dataKey="value" isAnimationActive={false}>
                      {drill.timeline.map((d, i) => (
                        <rect key={i} fill={CAL_COLORS[d.state] || CAL_COLORS[0]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="text-[10px] text-muted flex justify-between font-mono-tabular mt-1">
                <span>D-90</span><span>D-60</span><span>D-30</span><span>Today</span>
              </div>
            </Tile>
          )}

          {hotspotType === "replen" && drill.forecast && (
            <Tile title="12-Week Demand Projection" testid="inv-drill-forecast">
              <div style={{ height: 200 }}>
                <ResponsiveContainer>
                  <LineChart data={drill.forecast} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid stroke="rgb(var(--border))" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="week" tick={{ fontSize: 10, fill: "rgb(var(--muted))" }} />
                    <YAxis tick={{ fontSize: 10, fill: "rgb(var(--muted))" }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="demand" stroke="rgb(var(--chart-1))" strokeWidth={1.8} dot={false} isAnimationActive={false} name="Demand" />
                    <Line type="monotone" dataKey="onHandProjected" stroke="rgb(var(--chart-2))" strokeWidth={1.8} strokeDasharray="3 3" dot={false} isAnimationActive={false} name="On-hand projected" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {drill.supplierContext && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                  <div><div className="text-muted">Supplier</div><div className="font-medium">{drill.supplierContext.supplier}</div></div>
                  <div><div className="text-muted">Lead Time</div><div className="font-mono-tabular">{drill.supplierContext.leadTime}</div></div>
                  <div><div className="text-muted">MOQ</div><div className="font-mono-tabular">{drill.supplierContext.moq}</div></div>
                  <div><div className="text-muted">OTD (90d)</div><div className="font-mono-tabular text-success">{drill.supplierContext.otd}</div></div>
                </div>
              )}
            </Tile>
          )}

          {hotspotType === "deadstock" && drill.scenarios && (
            <Tile title="Recovery Scenarios" testid="inv-drill-scenarios">
              <div className="space-y-2">
                {drill.scenarios.map((s, i) => (
                  <div key={i} data-testid={`inv-scenario-${i}`}
                       className={cn("p-3 border rounded-sm flex items-center gap-3",
                         s.recommended ? "border-primary/50 bg-primary/5" : "border-border")}>
                    <div className={cn("w-8 h-8 rounded-sm flex items-center justify-center shrink-0",
                      s.recommended ? "bg-primary/15 text-primary" : "bg-surface-2 text-muted")}>
                      {s.name.startsWith("Pooled") ? <ArrowRightLeft size={14} /> :
                       s.name.startsWith("Shopee") ? <Trash2 size={14} /> :
                       <Sparkles size={14} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{s.name}</span>
                        {s.recommended && <Badge status="success">Recommended</Badge>}
                      </div>
                      <div className="text-[11px] text-muted mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5">
                        <span>Timeline: <span className="text-fg">{s.timeline}</span></span>
                        <span>Risk: <span className="text-fg">{s.risk}</span></span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono-tabular text-sm font-semibold">{s.recovery > 0 ? formatPeso(s.recovery, { compact: true }) : "—"}</div>
                      <div className="text-[10px] text-muted">recovery</div>
                    </div>
                  </div>
                ))}
              </div>
            </Tile>
          )}

          {/* Contributors table (all types except deadstock which uses scenarios) */}
          {drill.contributors && hotspotType !== "deadstock" && (
            <Tile title="Top Contributors" testid="inv-drill-contributors">
              <div className="space-y-1.5">
                {drill.contributors.slice(0, 6).map((c, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="text-[11px] text-muted font-mono-tabular w-5 text-right shrink-0">#{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium truncate">{c.name || c.sku}</div>
                      {(c.velocity || c.doh !== undefined) && (
                        <div className="text-[10px] text-muted">{c.velocity}{c.doh !== undefined ? ` · DoH ${c.doh}d` : ""}{c.days !== undefined ? ` · ${c.days} stockout days` : ""}</div>
                      )}
                      {c.action && <div className="text-[10px] text-primary mt-0.5">{c.action}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono-tabular text-[12px] font-semibold">
                        {typeof c.value === "number" ? (c.value > 1000 ? formatPeso(c.value, { compact: true }) : c.value.toLocaleString()) : c.value}
                      </div>
                      {typeof c.share === "number" && (
                        <div className="text-[9px] text-muted font-mono-tabular">{(c.share * 100).toFixed(1)}%</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Tile>
          )}

          {/* AI Explanation */}
          <Tile title="AI Explanation" testid="inv-drill-ai"
                action={drill.confidence ? <Badge status="info">Confidence {(drill.confidence * 100).toFixed(0)}%</Badge> : null}>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-sm bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <Sparkles size={14} />
              </div>
              <div className="flex-1">
                <p className="text-[12.5px] text-fg/95 leading-relaxed">{drill.aiExplanation}</p>
                <button data-testid="ask-copilot-inv-drill"
                  onClick={() => askCopilot(briefing)}
                  className="mt-2.5 inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium">
                  <MessageSquare size={11} /> Ask Copilot for full briefing →
                </button>
              </div>
            </div>
          </Tile>

          {/* Next Best Actions */}
          {drill.nba && drill.nba.length > 0 && (
            <Tile title="AI Recommendations · Next Best Actions" testid="inv-drill-nba">
              <div className="space-y-2">
                {drill.nba.map((a, i) => (
                  <div key={i} data-testid={`inv-nba-${i}`} className="p-3 border border-border rounded-sm bg-surface-2/40 flex items-start gap-3">
                    <div className={cn("w-2 h-2 rounded-full shrink-0 mt-1.5",
                      a.urgency === "critical" ? "bg-critical animate-pulse" :
                      a.urgency === "high" ? "bg-critical" :
                      a.urgency === "medium" ? "bg-warning" : "bg-info")} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium leading-snug">{a.title}</div>
                      <div className="text-[10px] text-muted mt-0.5 flex flex-wrap gap-x-3">
                        <span>Owner: <span className="text-fg">{a.owner}</span></span>
                        <span>Urgency: <Badge status={a.urgency === "critical" || a.urgency === "high" ? "critical" : a.urgency === "medium" ? "warning" : "info"}>{a.urgency}</Badge></span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase text-muted tracking-wider">Impact</div>
                      <div className="font-mono-tabular text-sm font-semibold text-success">{formatPeso(a.impact, { compact: true })}</div>
                      <button data-testid={`inv-nba-approve-${i}`}
                              className="mt-1 text-[10px] px-2 py-0.5 border border-success/40 text-success hover:bg-success/10 rounded-sm">
                        Approve
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Tile>
          )}
        </div>
      </aside>
    </>
  );
}
